/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { getInvalidFields, isEntityInvalid, setEntityField, setEntityFields } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { find, first, includes, intersection } from 'lodash';
import {
  getJeComponentId,
  getJeCostCenterId,
  getJeCuttingListItemId,
  getJeItemId,
  getJeTopId,
  isJeCcFiltered,
  isJeComponentCostCenter,
  isJeElementHasInvalidFormula,
  JeCcFilter,
  JeComponent,
  JeCostCenter,
  JeCuttingListItem,
  JeItem,
  JeTop,
} from '../../../resources/job-estimate-details.model';
import {
  jeCcTable,
  JeComponentDetailsTab,
  JeDetailsSelection,
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  JeFocusedTable,
  queryJeBestComponentDetailsTab,
  queryJeBestItemDetailsTab,
  queryJeCcHasErrors,
  queryJeCostCenter,
  queryJeCostCenters,
  queryJeFirstComponent, queryJeFirstComponentAmong,
  queryJeFirstCostCenterItem, queryJeFirstCostCenterItemAmong,
  queryJeFirstInvalidComponent,
  queryJeFirstInvalidItem,
  queryJeFirstInvalidTop,
  queryJeItem,
  queryJeSelectedCostCenterId,
  queryJeSelectedElement,
  queryJeTop,
} from '../je-details.state';

const GRID_VALIDATATABLE_FIELDS = ['quantity', 'cost', 'description', 'orderId'];

const highlightInvalidFormula = (selection: JeDetailsSelection, highlight: boolean) =>
  setEntityField(selection, 'highlightInvalidFormula', highlight);

const selectCC = (selection: JeDetailsSelection, ccId?: ID) => setEntityField(selection, 'costCenterId', ccId);
const selectCCItem = (selection: JeDetailsSelection, itemId?: ID) =>
  setEntityFields(selection, { itemId, componentId: void 0 });
const selectComponent = (selection: JeDetailsSelection, componentId?: ID) =>
  setEntityFields(selection, { componentId, itemId: void 0 });
const selectDetailsTab = (selection: JeDetailsSelection, name?: string) =>
  highlightInvalidFormula(setEntityField(selection, 'detailsTabName', name), false);
const setFocusedCell = (selection: JeDetailsSelection, table?: JeFocusedTable, cell?: FocusedCell) =>
  setEntityFields(selection, {
    'focusedTable': table,
    'focusedCell': cell,
  });
const resetFocusedCell = (selection: JeDetailsSelection) => setFocusedCell(selection, void 0, void 0);

export function selectionReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterSetFilter:
      return selectCcForFilter(setCcFilter(state, event.filter));
    default:
      return setFilterForSelectedCc(setEntityField(state, 'selection', navigate(state, state.selection, event)));
  }
}

/**
 * Guarantees that selection always hold references to existing entities.
 * It is important to remove references to removed entities.
 */
export function selectionConsistencyReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  const { selection } = state;
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterItemAddSet: {
      const itemToSelect = queryJeFirstCostCenterItemAmong(state, event.itemSet);
      const itemId = itemToSelect ? getJeItemId(itemToSelect) : void 0;
      return setEntityField(state, 'selection', navigateToCCItem(state, state.selection, itemId));
    }
    case JeDetailsStateEventType.CostCenterItemChangeCc:
    case JeDetailsStateEventType.CostCenterItemRemove:
      if (selection.itemId === event.itemId) {
        return setEntityField(state, 'selection', selectCCItem(selection));
      } else {
        return state;
      }
    case JeDetailsStateEventType.ComponentAddSet: {
      queryJeFirstComponent(state);
      const componentToSelect = queryJeFirstComponentAmong(state, event.componentSet.components);
      const componentId = componentToSelect ? getJeComponentId(componentToSelect) : void 0;
      return setEntityField(state, 'selection', navigateToComponent(state, state.selection, componentId));
    }
    case JeDetailsStateEventType.ComponentRemove:
      if (selection.componentId === event.componentId) {
        return setEntityField(state, 'selection', selectComponent(selection));
      } else {
        return state;
      }
    default:
      return state;
  }
}

function navigate(state: JeDetailsState,
                  selection: JeDetailsSelection,
                  event: JeDetailsStateEvent): JeDetailsSelection {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterSelect:
      const costCenter = event.costCenterId && jeCcTable.byId(state.jobCostCenters, event.costCenterId);

      if (costCenter && isJeComponentCostCenter(costCenter)) {
        return naviateToComponentsCC(state, selection);
      } else if (costCenter) {
        return navigateToCC(state, selection, event.costCenterId);
      } else {
        return state.selection;
      }
    case JeDetailsStateEventType.ComponentSelect:
      return navigateToComponent(state, selection, event.componentId);
    case JeDetailsStateEventType.CostCenterItemSelect:
      return navigateToCCItem(state, selection, event.itemId);
    case JeDetailsStateEventType.DetailsTabSelect:
      return selectDetailsTab(selection, event.name);
    case JeDetailsStateEventType.NavigateToInvalid:
      return navigateToInvalid(state);
    case JeDetailsStateEventType.NavigateToInvalidTop: {
      const top = queryJeTop(state, event.topId);
      return top ? navigateToInvalidTop(state, top) : selection;
    }
    case JeDetailsStateEventType.HighlightInvalid:
      return highlightInvalid(state);
    case JeDetailsStateEventType.ResetFocusedCell:
      return resetFocusedCell(state.selection);
    default:
      return selection;
  }
}

function setCcFilter(state: JeDetailsState, filter: JeCcFilter): JeDetailsState {
  return setEntityField(state, 'ccFilter', filter);
}

function selectCcForFilter(state: JeDetailsState): JeDetailsState {
  const ccId = queryJeSelectedCostCenterId(state);
  const cc = ccId && queryJeCostCenter(state, ccId);
  const ccToBeSelected = cc && isJeCcFiltered(cc, state.ccFilter)
    ? cc : find<JeCostCenter>(queryJeCostCenters(state), (filteredCc) => isJeCcFiltered(filteredCc, state.ccFilter));
  return ccToBeSelected === cc ? state :
    setEntityField(
      state,
      'selection',
      navigateToCC(state, state.selection, ccToBeSelected ? getJeCostCenterId(ccToBeSelected) : void 0));
}

function setFilterForSelectedCc(state: JeDetailsState): JeDetailsState {
  const ccId = queryJeSelectedCostCenterId(state);
  const cc = ccId && queryJeCostCenter(state, ccId);
  return cc && isJeCcFiltered(cc, state.ccFilter) ? state : setCcFilter(state, JeCcFilter.ShowAll);
}

function navigateToCC(state: JeDetailsState,
                      selection: JeDetailsSelection,
                      costCenterId?: ID): JeDetailsSelection {
  const firstItem = queryJeFirstCostCenterItem(state, costCenterId!); // tslint:disable-line:no-non-null-assertion

  return navigateToCCItem(state, selectCC(selection, costCenterId), firstItem ? getJeItemId(firstItem) : void 0);
}

function navigateToCCItem(state: JeDetailsState,
                          selection: JeDetailsSelection,
                          itemId?: ID): JeDetailsSelection {
  const item = itemId && queryJeItem(state, itemId);

  return item
    ? resetFocusedCell(selectDetailsTab(
      selectCCItem(selectCC(selection, item.costCenterId), itemId),
      queryJeBestItemDetailsTab(state, itemId)))
    : resetFocusedCell(selectDetailsTab(
      selectCCItem(selection, void 0),
      void 0));
}

function naviateToComponentsCC(state: JeDetailsState,
                               selection: JeDetailsSelection): JeDetailsSelection {
  // select first component
  const firstComponent = queryJeFirstComponent(state);

  return navigateToComponent(
    state,
    selection,
    firstComponent ? getJeComponentId(firstComponent) : void 0);
}

function navigateToComponent(state: JeDetailsState,
                             selection: JeDetailsSelection,
                             componentId: Maybe<ID>): JeDetailsSelection {
  return resetFocusedCell(
    selectDetailsTab(
      selectComponent(selectCC(selection, state.jobComponentsCcId), componentId),
      queryJeBestComponentDetailsTab(state, componentId)));
}

function navigateToInvalid(state: JeDetailsState): JeDetailsSelection {
  const invalidTop = queryJeFirstInvalidTop(state);
  if (invalidTop) {
    return navigateToInvalidTop(state, invalidTop);
  }

  const invalidComponent = queryJeFirstInvalidComponent(state);
  if (invalidComponent) {
    return navigateToInvalidComponent(state, invalidComponent);
  }

  const invalidCC = find(jeCcTable.all(state.jobCostCenters), (cc) => queryJeCcHasErrors(state, getJeCostCenterId(cc)));
  if (invalidCC) {
    const invalidItem = queryJeFirstInvalidItem(state, getJeCostCenterId(invalidCC));
    if (invalidItem) {
      return navigateToInvalidCCItem(state, invalidItem);
    }
  }

  return state.selection;
}

function navigateToInvalidTop(state: JeDetailsState, top: JeTop): JeDetailsSelection {
  return setFocusedCell(
    state.selection,
    JeFocusedTable.TopList,
    { id: getJeTopId(top), field: 'value' });
}

function navigateToInvalidComponent(state: JeDetailsState, component: JeComponent): JeDetailsSelection {
  const invalidFields = getInvalidFields(component);

  const componentSelection = selectDetailsTab(
    selectComponent(
      selectCC(state.selection, state.jobComponentsCcId),
      getJeComponentId(component)),
    queryJeBestComponentDetailsTab(state, getJeComponentId(component)));

  const simpleInvalidField = first(intersection(invalidFields, GRID_VALIDATATABLE_FIELDS));
  if (simpleInvalidField) {
    return setFocusedCell(
      componentSelection,
      JeFocusedTable.ComponentList,
      { id: getJeComponentId(component), field: simpleInvalidField });
  } else if (includes(invalidFields, 'formula')) {
    return highlightInvalidFormula(selectDetailsTab(componentSelection, JeComponentDetailsTab.Formula), true);
  } else if (includes(invalidFields, 'cuttingList')) {
    return navigateToInvalidCuttingListItem(
      selectDetailsTab(componentSelection, JeComponentDetailsTab.CuttingList),
      component.cuttingList || []);
  } else {
    return componentSelection;
  }
}

function navigateToInvalidCCItem(state: JeDetailsState, item: JeItem): JeDetailsSelection {
  const invalidFields = getInvalidFields(item);

  const itemSelection = selectDetailsTab(
    selectCCItem(
      selectCC(state.selection, item.costCenterId),
      getJeItemId(item)),
    queryJeBestItemDetailsTab(state, getJeItemId(item)));

  const simpleInvalidField = first(intersection(invalidFields, GRID_VALIDATATABLE_FIELDS));
  if (simpleInvalidField) {
    return setFocusedCell(
      itemSelection,
      JeFocusedTable.ItemList,
      { id: getJeItemId(item), field: simpleInvalidField });
  } else if (includes(invalidFields, 'formula')) {
    return highlightInvalidFormula(selectDetailsTab(itemSelection, JeComponentDetailsTab.Formula), true);
  } else if (includes(invalidFields, 'cuttingList')) {
    return navigateToInvalidCuttingListItem(
      selectDetailsTab(itemSelection, JeComponentDetailsTab.CuttingList),
      item.cuttingList || []);
  } else {
    return itemSelection;
  }
}

function navigateToInvalidCuttingListItem(selection: JeDetailsSelection,
                                          cuttingList: JeCuttingListItem[]): JeDetailsSelection {
  const cuttingListItem = find<JeCuttingListItem>(cuttingList, isEntityInvalid);

  if (cuttingListItem) {
    return setFocusedCell(selection, JeFocusedTable.CuttingList, {
      id: getJeCuttingListItemId(cuttingListItem),
      field: getInvalidFields(cuttingListItem)[0],
    });
  } else {
    return selection;
  }
}

/**
 * Highlights invalid data in current selection
 */
function highlightInvalid(state: JeDetailsState): JeDetailsSelection {
  const selectedStructure = queryJeSelectedElement(state);

  if (isNotNil(selectedStructure) && isJeElementHasInvalidFormula(selectedStructure)) {
    return highlightInvalidFormula(state.selection, true);
  } else {
    return state.selection;
  }
}
